// src/specialDates.js
//
// CENTRALIZED DATE / CUTOFF CONFIGURATION (2026 season)
// -----------------------------------------------------
// Everything to do with "is this meal still bookable?" lives here.
// App.js and HotelCheckin.jsx both import isMealPastCutoff from here so the
// answer is identical no matter which screen the member uses.
//
// ChefDashboard intentionally does NOT import this cutoff function — chef has
// a hard override (`const isMealPastCutoff = () => false;`). That override is
// part of the contract. Do not replace it.
//
// Dates expected in YYYY-MM-DD (en-CA format).
// All time math assumes America/Chicago.
//
// SEASON WINDOW
// The club serves meals from Memorial Day weekend through Labor Day lunch.
// BOTH ends are computed per year (see the SEASON block below), so the season
// rolls over on its own — there is no yearly code edit to forget.
//
// The only thing that needs updating each year is the special-event date
// tables further down, because those dates genuinely move. If they are ever
// left stale, the normal summer season still works; only the events go quiet.

import { todayCST, cstFmtYMD } from './config';

// ---------- helpers ----------
const ymdToDate = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Exported version for consumers that want a real Date from a ymd string.
export const ymdToLocalDate = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const cutoffOnSelected = (ymd, hour, minute = 0) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  return new Date(y, m - 1, d, hour, minute, 0, 0);
};

const inRange = (ymd, start, end) => {
  const s = String(start);
  const e = String(end);
  const d = String(ymd);
  return d >= s && d <= e;
};

// First Monday in September (Labor Day) for a given year, as YYYY-MM-DD.
export const laborDayYmd = (year) => {
  const sept1 = new Date(year, 8, 1);
  const offset = (1 - sept1.getDay() + 7) % 7; // days from Sep 1 to the first Monday
  const labor = new Date(year, 8, 1 + offset);
  return `${labor.getFullYear()}-09-${String(labor.getDate()).padStart(2, '0')}`;
};

// Last Monday in May (Memorial Day) for a given year, as YYYY-MM-DD.
export const memorialDayYmd = (year) => {
  const may31 = new Date(year, 4, 31);
  const back = (may31.getDay() - 1 + 7) % 7; // days back from May 31 to a Monday
  const memorial = new Date(year, 4, 31 - back);
  return `${memorial.getFullYear()}-05-${String(memorial.getDate()).padStart(2, '0')}`;
};

// First Monday in September (Labor Day).
export const isLaborDay = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return false;
  return String(ymd).trim() === laborDayYmd(y);
};

const NORMAL = {
  label: { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner' },
  enabled: { breakfast: true, lunch: true, dinner: true },
  banner: null,
};

const weekendPattern = (friYmd, satYmd, sunYmd, banner) => ({
  [friYmd]: {
    label: { breakfast: null, lunch: null, dinner: 'Dinner' },
    enabled: { breakfast: false, lunch: false, dinner: true },
    banner,
  },
  [satYmd]: {
    label: { breakfast: 'Brunch', lunch: 'Cocktails', dinner: 'Dinner' },
    enabled: { breakfast: true, lunch: true, dinner: true },
    banner,
  },
  [sunYmd]: {
    label: { breakfast: 'Brunch', lunch: null, dinner: null },
    enabled: { breakfast: true, lunch: false, dinner: false },
    banner,
  },
});

// ---------- SPECIAL MEAL CONFIGS ----------
// Every date listed here stays bookable after the season closes — see
// POST_SEASON_EVENT_DATES below. Adding a weekend here is all it takes to
// open it back up.
const SPECIAL_MEALS = {
  // Final Fling moved from Oct 31 – Nov 2 to Oct 23 – 25 (a true Fri/Sat/Sun).
  ...weekendPattern('2026-10-23', '2026-10-24', '2026-10-25', 'Final Fling Weekend'),
  ...weekendPattern('2026-04-24', '2026-04-25', '2026-04-26', 'Stag Weekend'),
  ...weekendPattern('2026-05-01', '2026-05-02', '2026-05-03', 'WOW Weekend'),

  // Thanksgiving — Thursday, Nov 26 2026. Dinner only; no breakfast or lunch
  // service that day. Same shape as a special-weekend Friday.
  '2026-11-26': {
    label: { breakfast: null, lunch: null, dinner: 'Thanksgiving Dinner' },
    enabled: { breakfast: false, lunch: false, dinner: true },
    banner: 'Thanksgiving',
  },
};

// ---------- SPECIAL WEEKEND BOOKING DEADLINES ----------
// Stored as Central time with explicit offset. April and October = CDT (-05:00);
// DST does not end until Nov 1, 2026.
const SPECIAL_WEEKEND_BOOKING_DEADLINES = {
  // Final Fling Weekend (Oct 23-25) cutoff: Mon Oct 19 @ 5:00 PM Central
  '2026-10-23': '2026-10-19T17:00:00-05:00',
  '2026-10-24': '2026-10-19T17:00:00-05:00',
  '2026-10-25': '2026-10-19T17:00:00-05:00',

  // Stag Weekend (Apr 24-26) cutoff: Apr 20 @ 5:00 PM Central
  '2026-04-24': '2026-04-20T17:00:00-05:00',
  '2026-04-25': '2026-04-20T17:00:00-05:00',
  '2026-04-26': '2026-04-20T17:00:00-05:00',

  // WOW Weekend (May 1-3) cutoff: Apr 23 @ 5:00 PM Central
  '2026-05-01': '2026-04-23T17:00:00-05:00',
  '2026-05-02': '2026-04-23T17:00:00-05:00',
  '2026-05-03': '2026-04-23T17:00:00-05:00',
};

// ---------- SINGLE-DAY DINNER EVENTS ----------
const DINNER_EVENT_LABELS = {
  '2026-03-14': 'Spring Break Hike & Potluck',
  '2026-04-05': 'Easter Dinner',
  '2026-08-08': 'August Party Dinner',
};

// ---------- SEASON ----------
// The season opens on the Friday of Memorial Day weekend and closes after
// Labor Day LUNCH — Labor Day dinner is already out of season.
//
// Both ends are derived from the year of the date being checked, so this is
// correct for every year without edits. 2026 works out to May 22 → Sep 7,
// which matches the 'Memorial Day Weekend' and 'Labor Day Weekend' banner
// ranges below.
//
// Outside that window only the special event dates (SPECIAL_MEALS +
// DINNER_EVENT_LABELS) can be booked — that is how Stag and WOW work in April
// and how Final Fling and Thanksgiving work in the fall. Booking history bears
// this out: every pre-season booking on record falls on one of those dates.
const SEASON_OPEN_OFFSET_DAYS = 3; // Memorial Day (Mon) minus 3 = that Friday
const SEASON_LAST_MEAL = 'lunch';

export const seasonOpenYmd = (year) => {
  const d = ymdToLocalDate(memorialDayYmd(year));
  d.setDate(d.getDate() - SEASON_OPEN_OFFSET_DAYS);
  // Built from local parts on purpose — no timezone conversion, same as the
  // Labor Day / Memorial Day helpers above.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const seasonCloseYmd = (year) => laborDayYmd(year);

const MEAL_ORDER = { breakfast: 0, lunch: 1, dinner: 2 };

// Dates bookable outside the season window. Derived, not hand-maintained: add
// a weekend to SPECIAL_MEALS or a dinner to DINNER_EVENT_LABELS and it opens.
const SPECIAL_EVENT_DATES = new Set([
  ...Object.keys(SPECIAL_MEALS),
  ...Object.keys(DINNER_EVENT_LABELS),
]);

// ---------- BANNER RANGES ----------
const BANNER_RANGES = [
  { start: '2026-05-16', end: '2026-05-17', banner: 'Work Weekend', mondayBreakfastAllowed: false },
  { start: '2026-05-22', end: '2026-05-25', banner: 'Memorial Day Weekend', mondayBreakfastAllowed: true },
  { start: '2026-06-20', end: '2026-06-20', banner: 'Float Trip Day', mondayBreakfastAllowed: false },
  { start: '2026-07-03', end: '2026-07-06', banner: '4th of July Weekend', mondayBreakfastAllowed: true },
  { start: '2026-09-04', end: '2026-09-07', banner: 'Labor Day Weekend', mondayBreakfastAllowed: true },
];

// ---------- NOTES ----------
const SPECIAL_NOTES = {
  '2026-03-14':
    'Spring Break Hike & Potluck (uses Dinner booking). Breakfast and lunch are still available.',
  '2026-04-05':
    'Easter (uses Dinner booking). Breakfast and lunch are still available.',
  '2026-08-08':
    'August Party (uses Dinner booking). Breakfast and lunch are still available.',
  '2026-11-26':
    'Thanksgiving Dinner. Dinner only — no breakfast or lunch service.',
};

// ---------- PUBLIC API ----------

export function getMealConfigForDate(ymd) {
  const dateStr = String(ymd || '').trim();
  if (!dateStr) return { ...NORMAL };

  let cfg = {
    label: { ...NORMAL.label },
    enabled: { ...NORMAL.enabled },
    banner: null,
  };

  for (const r of BANNER_RANGES) {
    if (inRange(dateStr, r.start, r.end)) {
      cfg.banner = r.banner || cfg.banner;
      break;
    }
  }

  if (SPECIAL_MEALS[dateStr]) {
    cfg = {
      label: { ...SPECIAL_MEALS[dateStr].label },
      enabled: { ...SPECIAL_MEALS[dateStr].enabled },
      banner: SPECIAL_MEALS[dateStr].banner ?? cfg.banner ?? null,
    };
  }

  if (DINNER_EVENT_LABELS[dateStr]) {
    cfg.label = { ...cfg.label, dinner: DINNER_EVENT_LABELS[dateStr] };
    if (!cfg.banner) cfg.banner = DINNER_EVENT_LABELS[dateStr];
  }

  return cfg;
}

export function specialLabelsFor(ymd) {
  const cfg = getMealConfigForDate(ymd);
  return { label: cfg.label, enabled: cfg.enabled };
}

export function getSpecialNoteForDate(ymd) {
  const dateStr = String(ymd || '').trim();
  if (!dateStr) return '';

  if (SPECIAL_NOTES[dateStr]) return SPECIAL_NOTES[dateStr];

  for (const r of BANNER_RANGES) {
    if (inRange(dateStr, r.start, r.end)) {
      return r.banner ? `${r.banner}.` : '';
    }
  }

  const cfg = getMealConfigForDate(dateStr);
  return cfg.banner ? `${cfg.banner}.` : '';
}

export function isMondayBreakfastAllowed(ymd) {
  const dateStr = String(ymd || '').trim();
  const dt = ymdToDate(dateStr);
  if (!dt) return false;

  const isMonday = dt.getDay() === 1;
  if (!isMonday) return false;

  for (const r of BANNER_RANGES) {
    if (r.mondayBreakfastAllowed && inRange(dateStr, r.start, r.end)) return true;
  }
  return false;
}

export function getSpecialBookingDeadlineForDate(ymd) {
  const dateStr = String(ymd || '').trim();
  if (!dateStr) return null;
  return SPECIAL_WEEKEND_BOOKING_DEADLINES[dateStr] || null;
}

// True when this meal falls outside the season and is therefore not bookable.
// See the SEASON block above for the rules.
export function isSeasonClosed(meal, ymd) {
  const dateStr = String(ymd || '').trim();
  if (!dateStr) return false;

  // Special events are bookable whatever time of year they land on — but only
  // for the meals the event actually serves (no breakfast on Thanksgiving).
  if (SPECIAL_EVENT_DATES.has(dateStr)) {
    const cfg = getMealConfigForDate(dateStr);
    return !cfg.enabled[meal] || !cfg.label[meal];
  }

  const year = Number(dateStr.slice(0, 4));
  if (!year) return false; // unparseable — leave it to the other rules

  // Before the season opens, or after Labor Day entirely.
  if (dateStr < seasonOpenYmd(year)) return true;
  const close = seasonCloseYmd(year);
  if (dateStr > close) return true;

  // Labor Day itself: bookable through SEASON_LAST_MEAL, then closed.
  if (dateStr === close) {
    return (MEAL_ORDER[meal] ?? 99) > (MEAL_ORDER[SEASON_LAST_MEAL] ?? 99);
  }

  return false; // in season
}

// Convenience for screens that want to flag a whole day as closed.
export function isDateSeasonClosed(ymd) {
  return ['breakfast', 'lunch', 'dinner'].every((m) => isSeasonClosed(m, ymd));
}

// ==========================================================================
// SHARED CUTOFF LOGIC
// --------------------------------------------------------------------------
// Single source of truth. App.js and HotelCheckin.jsx both import this.
// If you need to tweak a cutoff time, change it here — only here.
//
// Rules:
//   0) Season window. Memorial Day weekend through Labor Day LUNCH — that is
//      the last bookable meal on a normal date. Labor Day dinner and anything
//      outside the window is closed, except the special event dates, which
//      fall through to the rules below. Both ends are computed per year.
//   1) Special weekend deadlines (e.g. Stag, WOW) override everything.
//   2) Breakfast is blocked on Mondays unless Labor Day or a banner range
//      with mondayBreakfastAllowed = true covers that date. Currently the
//      only Monday exceptions are Memorial Day, 4th of July, and Labor Day
//      weekends.
//   3) Breakfast is unconditionally blocked on Tuesdays. No exceptions.
//      (Chef and Admin Act-As still bypass via their own overrides.)
//   4) Same-day cutoffs:
//        breakfast: 9:00 AM
//        lunch:     9:30 AM
//        dinner:    3:30 PM
//   5) Tomorrow's breakfast locks at 9:00 PM tonight.
// ==========================================================================
const NORMAL_CUTOFFS = {
  breakfast: { h: 9, m: 0 },
  lunch:     { h: 9, m: 30 },
  dinner:    { h: 15, m: 30 },
};

// The optional 4th arg `bypassCutoff` is used by Admin Act-As (and anywhere
// else we want an authorized role to enter late bookings). When true, every
// cutoff / Monday-breakfast / weekend-deadline rule is skipped. Chef has its
// own hardcoded bypass in ChefDashboard — this keeps that pattern consistent.
export function isMealPastCutoff(meal, rowDate, now = new Date(), bypassCutoff = false) {
  if (bypassCutoff) return false;

  const today = todayCST();

  // 0) Season window. Special event dates are never season-closed, so they
  //    fall through to the rules below.
  if (isSeasonClosed(meal, rowDate)) return true;

  // 1) Special weekend override
  const specialDeadline = getSpecialBookingDeadlineForDate(rowDate);
  if (specialDeadline) {
    return now > new Date(specialDeadline);
  }

  const dow = ymdToLocalDate(rowDate).getDay(); // 0=Sun..6=Sat

  // 2) Monday breakfast blocked unless Labor Day or allowed by banner range
  //    (Memorial Day weekend, 4th of July weekend, Labor Day weekend).
  if (
    meal === 'breakfast' &&
    dow === 1 &&
    !isLaborDay(rowDate) &&
    !isMondayBreakfastAllowed(rowDate)
  ) {
    return true;
  }

  // 2b) Tuesday breakfast is unconditionally blocked.
  if (meal === 'breakfast' && dow === 2) {
    return true;
  }

  // 3) Same-day cutoffs
  if (rowDate === today) {
    const c = NORMAL_CUTOFFS[meal];
    return c ? now > cutoffOnSelected(today, c.h, c.m) : false;
  }

  // 4) Tomorrow breakfast locks at 9 PM today
  const t = ymdToLocalDate(today);
  t.setDate(t.getDate() + 1);
  const tomorrow = cstFmtYMD.format(t);
  if (rowDate === tomorrow && meal === 'breakfast') {
    return now > cutoffOnSelected(today, 21, 0);
  }

  return false;
}

// ESLint-safe default export (existing code depends on this shape)
const specialDates = {
  SPECIAL_MEALS,
  SPECIAL_WEEKEND_BOOKING_DEADLINES,
  DINNER_EVENT_LABELS,
  BANNER_RANGES,
  SPECIAL_NOTES,
  SPECIAL_EVENT_DATES,
};

export default specialDates;
